import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  warehouseOrders,
  type WarehouseOrderListItem,
  type WarehouseOrderDirection,
  type WarehouseOrderState,
} from '@/api/warehouseOrders';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';

type DirectionFilter = 'ALL' | WarehouseOrderDirection;
type StateFilter = 'ALL' | WarehouseOrderState;

const DIRECTION_LABEL: Record<WarehouseOrderDirection, string> = {
  IN: 'Приходний',
  OUT: 'Видатковий',
};

const STATE_LABEL: Record<WarehouseOrderState, string> = {
  DRAFT: 'Чернетка',
  COMMITTEE_VERIFIED: 'Перевірено комітетом',
  EXECUTED: 'Виконаний',
  CANCELLED: 'Скасований',
};

export default function WarehouseOrdersPage() {
  const navigate = useNavigate();
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('ALL');
  const [stateFilter, setStateFilter] = useState<StateFilter>('ALL');
  const f = useDocListFilters();

  // 2026-08-11 (Норма #29, субменю Склад): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<WarehouseOrderListItem>(
    (limit, offset, sort) =>
      warehouseOrders.list({
        limit, offset,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        organization_id: f.orgId || undefined,
        date_from: f.dateFrom, date_to: f.dateTo,
        ...(directionFilter !== 'ALL' ? { direction: directionFilter } : {}),
        ...(stateFilter !== 'ALL' ? { state: stateFilter } : {}),
      }),
    [directionFilter, stateFilter, ...f.queryKey],
    { attachmentsType: 'doc.warehouse_orders' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<WarehouseOrderListItem>('doc.warehouse_orders', f.orgId);

  const cols = useMemo<ColDef<WarehouseOrderListItem>[]>(
    () => [
      {
        headerName: '', width: 44, sortable: false,
        cellRenderer: (p: { data?: WarehouseOrderListItem }) =>
          <ImportedBadge source={p.data?.external_source} />,
      },
      { field: 'document_number', headerName: '№', width: 150, sortable: true },
      {
        field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
        valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
      },
      {
        field: 'direction', headerName: 'Напрямок', width: 130, sortable: false,
        valueFormatter: (p) => p.value ? (DIRECTION_LABEL[p.value as WarehouseOrderDirection] ?? p.value) : '',
      },
      {
        field: 'state', headerName: 'Стан', flex: 1, minWidth: 180, sortable: true,
        valueFormatter: (p) => p.value ? (STATE_LABEL[p.value as WarehouseOrderState] ?? p.value) : '',
      },
      {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
        field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
      },
    ],
    [],
  );

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Складські ордери<TitleTotal total={total} /></h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem' }}>Напрямок:</label>
          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value as DirectionFilter)}
            data-testid="wo-direction-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="IN">Приходні</option>
            <option value="OUT">Видаткові</option>
          </select>
          <label style={{ fontSize: '0.875rem' }}>Стан:</label>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            data-testid="wo-state-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="DRAFT">Чернетки</option>
            <option value="COMMITTEE_VERIFIED">Перевірені комітетом</option>
            <option value="EXECUTED">Виконані</option>
            <option value="CANCELLED">Скасовані</option>
          </select>
        </div>
        <button
          className="primary"
          onClick={() => navigate('/warehouse/orders/new')}
          style={{ marginLeft: 'auto' }}
        >
          + Новий ордер
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[{ value: '', label: 'Усі статуси' }]} />
      </DocListFiltersRow>
      <InfiniteDocGrid<WarehouseOrderListItem>
        deleteRow={{ remove: (id) => warehouseOrders.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="warehouse-orders"
        onRowDoubleClicked={(r) => navigate(`/warehouse/orders/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
